import { Button, MonoLabel, Toggle } from '@/design-system';
import { ClientAddressFields, type AddressLookup } from '@/shared/ui';

import { inspectionsCopy as copy } from '../copy';

export interface ClientHomeFieldsProps {
    /** Het soort klant: een particulier heeft een woonadres, een bedrijf of makelaar een adres. */
    clientType: string;
    home: AddressLookup;
    /** Het adres dat al bij de gekozen klant staat, op één regel, of `null`. */
    savedAddress: string | null;
    /** Het bewaarde adres wordt aangepast: de velden staan open. */
    changing: boolean;
    onChange: () => void;
    sameAsObject: boolean;
    onSameAsObjectChange: (same: boolean) => void;
    /** Het adres van het object op één regel, zodra het af is. */
    objectAddress: string | null;
}

/**
 * Het huidige adres van de klant in "Nieuwe keuring": waar de factuur heen gaat,
 * en meestal niet het object.
 *
 * Een klant die al een adres heeft, laat dat adres op één regel zien: bij de
 * meeste keuringen verandert het niet, en vijf ingevulde velden zijn dan alleen
 * onrust. "Adres wijzigen" zet de velden open, al ingevuld. Verder zoekt het blok
 * op zoals het object ({@link ClientAddressFields}), en voor een verkoper of een
 * eigenaar die zelf in het pand woont neemt "Zelfde als keuringsadres" het adres
 * van het object over.
 */
export function ClientHomeFields({
    clientType,
    home,
    savedAddress,
    changing,
    onChange,
    sameAsObject,
    onSameAsObjectChange,
    objectAddress,
}: ClientHomeFieldsProps) {
    return (
        <fieldset className="m-0 grid min-w-0 gap-3 border-0 p-0 pt-2">
            <legend className="float-left mb-1 w-full p-0">
                <MonoLabel size={10} tone="muted">
                    {copy.newClientHomeSection(clientType)}
                </MonoLabel>
            </legend>

            {savedAddress !== null && !changing && !sameAsObject ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="m-0 min-w-0 text-[14px] leading-[1.4] text-[var(--text-primary)]">
                        {savedAddress}
                    </p>
                    <Button variant="outline" size="md" onClick={onChange}>
                        {copy.newClientHomeChange}
                    </Button>
                </div>
            ) : (
                <>
                    <Toggle
                        checked={sameAsObject}
                        onCheckedChange={onSameAsObjectChange}
                        label={copy.newClientHomeSameAsObject}
                        description={
                            sameAsObject
                                ? copy.newClientHomeFromObject(objectAddress)
                                : copy.newClientHomeSameAsObjectHint
                        }
                    />
                    {sameAsObject ? null : <ClientAddressFields address={home} />}
                </>
            )}
        </fieldset>
    );
}
